import { useEffect, useState } from 'react';

import { ENABLE_MAIL_SIGNATURE, ENABLE_MAIL_TONE } from '../config/features';
import { DataState } from '../context/DataProvider';
import { previewMail } from '../services/mailApi';

const DEBOUNCE_MS = 400;

/**
 * What the backend will do with a drafted mail when it is approved:
 * { audience, relation, signature_html, warnings } -- or null while loading,
 * when the features are off, or when the check fails (the mail still sends;
 * the preview is informational).
 *
 * Re-checks (debounced) as the user edits recipients or the body, since
 * adding one outside address turns an internal mail external.
 */
export default function useMailPreview({
  recipients,
  body,
  replyToMessageId,
  replyAll = false,
  attachmentCount = 0,
  enabled = true,
}) {
  const { userGoogleId: accessToken } = DataState();
  const [preview, setPreview] = useState(null);
  const recipientsKey = (recipients || []).join(',');

  useEffect(() => {
    if (!enabled || !accessToken) return undefined;
    if (!ENABLE_MAIL_SIGNATURE && !ENABLE_MAIL_TONE) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      previewMail(accessToken, {
        recipients: recipientsKey ? recipientsKey.split(',') : [],
        body: body || '',
        // A reply without its own recipients: the backend reads who the
        // original came from, so the audience is right.
        reply_to_message_id: replyToMessageId || undefined,
        reply_all: Boolean(replyAll),
        attachment_count: attachmentCount || 0,
      })
        .then((res) => !cancelled && setPreview(res?.data ?? null))
        .catch(() => !cancelled && setPreview(null));
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    accessToken,
    enabled,
    recipientsKey,
    body,
    replyToMessageId,
    replyAll,
    attachmentCount,
  ]);

  return preview;
}
